import React from "react";
import {
  Modal,
  Box,
  Typography,
  Backdrop,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import { GroupRemove } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import axiosInstance from "../../../api/axiosInstance";
import useNotification from "../../common/Notification";
import base_url from "../../../constants";

const CARD_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  maxWidth: 520,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 3,
  p: 4,
};

const WhatsappBusinessGroupDeleteContactModal = ({
  open,
  onClose,
  groupName,
  phoneNumbers = [],
  onSuccess,
}) => {
  const [loading, setLoading] = React.useState(false);
  const notify = useNotification();

  const handleRemove = async () => {
    if (!groupName || !phoneNumbers?.length) {
      notify("Group name and at least one phone number are required.", "error");
      return;
    }

    try {
      setLoading(true);
      const res = await axiosInstance.post(
        `${base_url}/whatsapp-business/groups/delete-phone-numbers`,
        {
          group_name: groupName,
          phone_numbers: phoneNumbers.map((p) => String(p).trim()).filter(Boolean),
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data?.status === "success" || res.data?.success) {
        notify(
          res.data?.message || "Contact(s) removed from group successfully",
          "success"
        );
        if (onSuccess) onSuccess();
        onClose();
      } else {
        notify(res.data?.message || "Failed to remove contact from group", "error");
      }
    } catch (err) {
      notify(
        err.response?.data?.message ||
          "Something went wrong while removing contact from group.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const displayPhones = phoneNumbers?.length
    ? phoneNumbers.map((p) => String(p).trim()).filter(Boolean)
    : [];
  const singlePhone = displayPhones.length === 1;

  return (
    <Modal
      aria-labelledby="delete-contact-from-group-modal-title"
      aria-describedby="delete-contact-from-group-modal-description"
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(15,23,42,0.55)",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={CARD_STYLE}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: "rgba(248,113,113,0.12)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <GroupRemove sx={{ fontSize: 20, color: "#b91c1c" }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        Remove contact from group
                      </Typography>
                    </Box>
                    <Button
                      onClick={onClose}
                      size="small"
                      sx={{ minWidth: "auto" }}
                    >
                      ✕
                    </Button>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    {singlePhone
                      ? "This will remove the following contact from the group. This action cannot be undone."
                      : "This will remove the following contacts from the group. This action cannot be undone."}
                  </Typography>

                  {groupName && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mr: 0.5 }}
                      >
                        Group:
                      </Typography>
                      <Chip
                        size="small"
                        label={groupName}
                        color="error"
                        variant="outlined"
                      />
                    </Box>
                  )}

                  {displayPhones.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mr: 0.5 }}
                      >
                        {singlePhone ? "Phone:" : "Phones:"}
                      </Typography>
                      {displayPhones.map((pn, i) => (
                        <Chip
                          key={i}
                          size="small"
                          label={pn}
                          sx={{
                            backgroundColor: "rgba(0,0,0,0.06)",
                            fontWeight: 500,
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    <Button
                      onClick={onClose}
                      size="small"
                      sx={{ textTransform: "none" }}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      component={motion.button}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        position: "relative",
                      }}
                      disabled={loading}
                      onClick={handleRemove}
                    >
                      Remove from group
                      {loading ? (
                        <CircularProgress
                          size={24}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            marginTop: "-12px",
                            marginLeft: "-12px",
                          }}
                        />
                      ) : null}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
};

export default WhatsappBusinessGroupDeleteContactModal;
